import { useEffect, useState, useRef } from 'react'; // quickreplies_image_v1
import API from '../api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Edit2, Tag, Image, X } from 'lucide-react';

export default function QuickReplies() {
  const [replies, setReplies] = useState([]);
  const [businesses, setBusinesses] = useState([]); // qr_business_v1
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ tag: '', title: '', content: '', file_url: null, file_type: null, business: '' }); // qr_business_v1
  const [filterTag, setFilterTag] = useState('all');
  const [filterBusiness, setFilterBusiness] = useState('all'); // qr_business_v1
  const [uploading, setUploading] = useState(false); // quickreplies_image_v1
  const fileInputRef = useRef(null); // quickreplies_image_v1

  const multiBiz = businesses.length >= 1; // qr_business_v1: มีหลายธุรกิจถึงจะโชว์ UI แยก (ร้านเดียว = เหมือนเดิม)

  // quickreplies_image_v1: append JWT for /api/messages/.../file URLs
  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api/')) {
      const token = localStorage.getItem('token');
      return token ? `${window.location.origin}${url}?t=${encodeURIComponent(token)}` : url;
    }
    return url;
  };

  // quickreplies_image_v1: upload image to /api/upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await API.post('/api/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, file_url: data.url, file_type: data.type }));
      toast.success('อัปโหลดสำเร็จ');
    } catch (err) {
      toast.error('อัปโหลดล้มเหลว');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const load = () => API.get('/api/quickreplies').then(r => setReplies(r.data));
  useEffect(() => {
    load();
    API.get('/api/quickreplies/businesses').then(r => setBusinesses(r.data || [])).catch(() => {}); // qr_business_v1
  }, []);

  const tags = ['all', ...new Set(replies.map(r => r.tag))];

  const emptyForm = { tag: '', title: '', content: '', file_url: null, file_type: null, business: '' }; // qr_business_v1

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await API.patch(`/api/quickreplies/${editing.id}`, form);
        toast.success('แก้ไขสำเร็จ');
      } else {
        await API.post('/api/quickreplies', form);
        toast.success('เพิ่มสำเร็จ');
      }
      setShow(false); setEditing(null); setForm(emptyForm); load();
    } catch { toast.error('เกิดข้อผิดพลาด'); }
  };

  const startEdit = (r) => { setEditing(r); setForm({ tag: r.tag, title: r.title, content: r.content, file_url: r.file_url, file_type: r.file_type, business: r.business || '' }); setShow(true); }; // qr_business_v1

  // qr_business_v1: กรองธุรกิจก่อน แล้วค่อยกรองหมวด
  //   เลือกธุรกิจหนึ่ง -> โชว์ของธุรกิจนั้น + ของส่วนกลาง (business ว่าง ใช้ได้ทุกธุรกิจ)
  const byBiz = filterBusiness === 'all' ? replies
    : filterBusiness === '__shared__' ? replies.filter(r => !r.business)
    : replies.filter(r => r.business === filterBusiness || !r.business);
  const filtered = filterTag === 'all' ? byBiz : byBiz.filter(r => r.tag === filterTag);

  const tagColors = ['#0ea5e9','#22c55e','#f59e0b','#ef4444','#a855f7','#ec4899','#14b8a6','#f97316'];
  const getTagColor = (tag) => tagColors[Math.abs(String(tag).split('').reduce((a,b) => a + b.charCodeAt(0), 0)) % tagColors.length];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b' }}>⚡ Quick Replies</h2>
        <button onClick={() => { setEditing(null); setForm(emptyForm); setShow(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
          <Plus size={16} />เพิ่มข้อความด่วน
        </button>
      </div>

      {/* qr_business_v1: ตัวกรองธุรกิจ (โชว์เฉพาะร้านที่มีหลายธุรกิจ) */}
      {multiBiz && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 700, marginRight: 2 }}>🏢 ธุรกิจ:</span>
          {['all', ...businesses, '__shared__'].map(b => {
            const label = b === 'all' ? 'ทุกธุรกิจ' : b === '__shared__' ? '🌐 ส่วนกลาง' : b;
            const cnt = b === 'all' ? replies.length : b === '__shared__' ? replies.filter(r => !r.business).length : replies.filter(r => r.business === b).length;
            const activeSel = filterBusiness === b;
            return (
              <button key={b} onClick={() => setFilterBusiness(b)}
                style={{ padding: '6px 14px', borderRadius: 8, border: activeSel ? '1px solid #0ea5e9' : '1px solid #e2e8f0', cursor: 'pointer', fontSize: 13, fontWeight: activeSel ? 700 : 500, background: activeSel ? '#0ea5e9' : '#fff', color: activeSel ? '#fff' : '#475569' }}>
                {label} ({cnt})
              </button>
            );
          })}
        </div>
      )}

      {/* Filter Tags (หมวดหมู่) */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {tags.map(t => (
          <button key={t} onClick={() => setFilterTag(t)}
            style={{ padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: filterTag === t ? 700 : 400, background: filterTag === t ? (t === 'all' ? '#1e293b' : getTagColor(t)) : '#f1f5f9', color: filterTag === t ? '#fff' : '#64748b' }}>
            {t === 'all' ? '📋 ทั้งหมด' : `🏷️ ${t}`} {t !== 'all' && `(${replies.filter(r => r.tag === t).length})`}
          </button>
        ))}
      </div>

      {/* List */}
      <div style={{ display: 'grid', gap: 10 }}>
        {filtered.map(r => (
          <div key={r.id} style={{ background: '#fff', borderRadius: 12, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderLeft: `4px solid ${getTagColor(r.tag)}` }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ background: getTagColor(r.tag) + '20', color: getTagColor(r.tag), padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Tag size={10} />{r.tag}
                </span>
                {/* qr_business_v1: ป้ายธุรกิจ */}
                {multiBiz && (
                  r.business
                    ? <span style={{ background: '#eef2ff', color: '#4f46e5', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>🏢 {r.business}</span>
                    : <span style={{ background: '#f1f5f9', color: '#64748b', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>🌐 ทุกธุรกิจ</span>
                )}
                <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{r.title}</span>
              </div>
              <p style={{ color: '#64748b', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{r.content}</p>
            </div>
            <div style={{ display: 'flex', gap: 6, marginLeft: 12 }}>
              <button onClick={() => startEdit(r)} style={{ padding: 8, background: '#f0f9ff', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#0ea5e9' }}>
                <Edit2 size={15} />
              </button>
              <button onClick={async () => { await API.delete(`/api/quickreplies/${r.id}`); load(); toast.success('ลบแล้ว'); }}
                style={{ padding: 8, background: '#fef2f2', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#ef4444' }}>
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 48, textAlign: 'center', color: '#94a3b8' }}>
            <p style={{ fontSize: 36, marginBottom: 12 }}>⚡</p>
            <p>ยังไม่มีข้อความด่วน กด "เพิ่มข้อความด่วน" เพื่อเริ่มต้น</p>
          </div>
        )}
      </div>

      {show && (
        <div style={{ position: 'fixed', inset: 0, background: '#00000060', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <h3 style={{ marginBottom: 20, fontWeight: 700, color: '#1e293b' }}>{editing ? '✏️ แก้ไขข้อความด่วน' : '⚡ เพิ่มข้อความด่วน'}</h3>
            <form onSubmit={submit}>
              {/* qr_business_v1: เลือกธุรกิจ (โชว์เฉพาะร้านหลายธุรกิจ) */}
              {multiBiz && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: '#64748b', fontSize: 13, display: 'block', marginBottom: 6 }}>🏢 ใช้กับธุรกิจไหน</label>
                  <select value={form.business || ''} onChange={e => setForm({ ...form, business: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', color: '#1e293b', fontSize: 14, background: '#fff' }}>
                    <option value="">🌐 ทุกธุรกิจ (ส่วนกลาง)</option>
                    {businesses.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <p style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>เลือก "ทุกธุรกิจ" = ใช้ร่วมทุกร้าน · เลือกธุรกิจ = เฉพาะร้านนั้น</p>
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ color: '#64748b', fontSize: 13, display: 'block', marginBottom: 6 }}>🏷️ Tag / หมวดหมู่ (เช่น ทักทาย, ฝากถอน, โปร)</label>
                <input value={form.tag} onChange={e => setForm({ ...form, tag: e.target.value })}
                  placeholder="เช่น ทักทาย"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', color: '#1e293b', fontSize: 14 }} required />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ color: '#64748b', fontSize: 13, display: 'block', marginBottom: 6 }}>📌 ชื่อข้อความ</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="เช่น ทักทายลูกค้าใหม่"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', color: '#1e293b', fontSize: 14 }} required />
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: '#64748b', fontSize: 13, display: 'block', marginBottom: 6 }}>💬 ข้อความ</label>
                <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={4}
                  placeholder="พิมพ์ข้อความที่ต้องการส่ง..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', color: '#1e293b', fontSize: 14, resize: 'vertical' }} required />
              </div>
              {/* quickreplies_image_v1: Image upload section */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ color: '#64748b', fontSize: 13, display: 'block', marginBottom: 6 }}>🖼️ รูปภาพแนบ (ไม่บังคับ)</label>
                {form.file_url ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 10, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
                    <img src={getFileUrl(form.file_url)} alt="preview" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6 }} />
                    <span style={{ flex: 1, fontSize: 13, color: '#0ea5e9' }}>แนบรูปไว้แล้ว</span>
                    <button type="button" onClick={() => setForm(f => ({ ...f, file_url: null, file_type: null }))}
                      style={{ padding: 6, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={16} /></button>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: '#f1f5f9', color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: 8, cursor: uploading ? 'wait' : 'pointer', fontSize: 13 }}>
                    <Image size={16} />{uploading ? 'กำลังอัปโหลด...' : 'เลือกรูปภาพ'}
                  </button>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={() => { setShow(false); setEditing(null); }}
                  style={{ flex: 1, padding: '10px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, cursor: 'pointer' }}>ยกเลิก</button>
                <button type="submit" style={{ flex: 1, padding: '10px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                  {editing ? 'บันทึก' : 'เพิ่ม'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
